import {
	InitialSetupArguments,
	LoginArguments,
	TwoFactorConfiguration,
	TwoFactorMethods,
} from 'Services/TwoFactor/Common';
import React, { FormEventHandler } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { runInAction } from 'mobx';
import { validate2faCode } from 'Services/Api/AuthorizationService';
import { TextInput } from 'Views/Components/New/TextInput';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';
import { Button } from 'Views/Components/Button/Button';
import alertToast from 'Util/ToastifyUtils';
import useAsync from 'Hooks/useAsync';
import Spinner from 'Views/Components/Spinner/Spinner';
import If from 'Views/Components/If/If';
import { ButtonGroup } from 'Views/Components/Button/ButtonGroup';
import { logout } from 'Util/NavigationUtils';
import { useLocation } from 'react-router-dom';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';

export const EmailTokenType = 'Email';
export interface EmailInitialSetupProps extends InitialSetupArguments {}

export const EmailInitialSetup = observer(
	({ close, configurePromise, loggedInUserName, userName }: EmailInitialSetupProps) => {
		const location = useLocation();
		const response = useAsync(() => configurePromise, []);
		const inputData = useLocalStore(() => ({
			code: '',
		}));
		const onSubmit: FormEventHandler = e => {
			e.preventDefault();
			validate2faCode({
				token: inputData.code,
				method: EmailTokenType,
			})
				.then(() => {
					alertToast('Successfully enabled two factor authentication', 'success');
					close();
				})
				.catch(() => {
					alertToast('Invalid two factor code', 'error');
				});
		};
		const onCloseAndLogout = () => {
			logout(location.pathname);
		};
		const onClose = () => {
			close();
		};
		if (response.type === 'loading') {
			return <LottieSpinner />;
		}
		if (response.type === 'error') {
			console.error(response.error);
			return (
				<div>
					<p>There was an error configuring email authentication.</p>
					<Button onClick={() => close(true)}>Close</Button>
				</div>
			);
		}
		return (
			<div>
				<h4>Email Authentication</h4>

				<If condition={userName === loggedInUserName}>
					<p>
						You now have two factor authentication configured for your account. To finish the setup process,
						you will have been sent an email with a code. Enter the code in the confirmation box below.
					</p>
					<p>If you do not enter the code you will be logged out from the application.</p>
					<form onSubmit={onSubmit}>
						<TextInput
							label="Authenticator Code"
							value={inputData.code ?? ''}
							onChange={e =>
								runInAction(() => {
									inputData.code = e.target.value;
								})
							}
						/>
						<ButtonGroup>
							<Button type="submit">Submit</Button>
							<Button onClick={onCloseAndLogout}>Close and logout</Button>
						</ButtonGroup>
					</form>
				</If>

				<If condition={userName !== loggedInUserName}>
					<p>The user {userName} now has two factor authentication enabled for their account.</p>
					<Button onClick={onClose}>Close</Button>
				</If>
			</div>
		);
	},
);
export interface EmailTwoFactorPageProps extends LoginArguments {}

export const EmailTwoFactorPage = observer(({ onTwoFactorSuccess, rememberMe }: EmailTwoFactorPageProps) => {
	const inputData = useLocalStore(() => ({
		code: '',
		rememberTwoFactor: false,
	}));
	const onSubmit: FormEventHandler = e => {
		e.preventDefault();
		validate2faCode({
			method: EmailTokenType,
			token: inputData.code,
			rememberTwoFactor: inputData.rememberTwoFactor,
			rememberMe: rememberMe,
		})
			.then(onTwoFactorSuccess)
			.catch(error => {
				alertToast(`Two factor error ${error}`, 'error');
			});
	};
	return (
		<div className="body-content">
			<form onSubmit={onSubmit}>
				<h2>Verify Login</h2>
				<div>
					You will have been sent an email with a code. Please enter that code and press continue to finish
					logging in.
				</div>
				<TextInput
					value={inputData.code ?? ''}
					onChange={e =>
						runInAction(() => {
							inputData.code = e.target.value;
						})
					}
				/>
				<CheckboxInput
					label="Skip this step for 14 days"
					checked={inputData.rememberTwoFactor ?? false}
					onChange={e =>
						runInAction(() => {
							inputData.rememberTwoFactor = e.target.checked;
						})
					}
				/>
				<Button type="submit">Submit</Button>
			</form>
		</div>
	);
});

export const EmailTwoFactorConfiguration: TwoFactorConfiguration = {
	onInitialSetup: args => ({
		content: <EmailInitialSetup {...args} />,
		required: true,
	}),
	onRemove: () => ({
		content: undefined,
		required: false,
	}),
	onLogin: args => ({
		content: <EmailTwoFactorPage {...args} />,
		required: true,
	}),
};
export function configureEmail2fa(methods: TwoFactorMethods) {
	methods[EmailTokenType] = EmailTwoFactorConfiguration;
}
